import { gql, useQuery } from '@apollo/client';
import LoadingIndicator from '@orn/shared-web/components/LoadingIndicator';
import * as React from 'react';
import { useParams } from 'react-router-dom';

import { CalloutPaneBody } from '../../components/CalloutCarousel';
import TransactionChangeIndicator from '../../components/changeIndicators/TransactionChangeIndicator';
import TransactionsChart from '../../components/charts/TransactionsChart';
import Entity from '../../components/entity';
import MerchantPaneHeader from '../../components/headers/MerchantPaneHeader/MerchantPaneHeader';
import { TransactionSummary } from '../../components/summaries';
import RecurringStreamTable from '../../components/tables/RecurringStreamTable';
import TransactionsTable, { TxnTableElementType } from '../../components/tables/TransactionsTable';
import { useFilteredTransactions } from '../../hooks/useFilteredTransactions';
import { useDateRangeParams } from '../../providers/DateRangeProvider';
import { calculateTotal, TransactionType } from '../../providers/TransactionsProvider';

import type { GetMerchantQuery, GetMerchantQueryVariables } from './__generated__/MerchantPane.gql';

const GET_MERCHANT = gql`
  query GetMerchant($id: ID!) {
    getMerchant(id: $id) {
      id
      name
      logoUrl
      isIncomeSource
      recurringTransactions {
        ...RecurringStream
      }
      ...MerchantPaneHeader
    }
  }
  ${MerchantPaneHeader.Fragment}
  ${RecurringStreamTable.Fragment}
`;

interface IMerchantPaneProps {
  // Stories pass this directly so they don't need a router with a matching route
  // pattern. Production rendering from MainApp.tsx omits the prop and the
  // useParams() fallback kicks in.
  merchantId?: string;
}

const MerchantPane = ({ merchantId: merchantIdProp }: IMerchantPaneProps = {}) => {
  const params = useParams();
  const merchantId = merchantIdProp ?? params.merchantId ?? '';

  // Date range synced with URL
  const [dateRange, setDateRange] = useDateRangeParams();

  // Fetch merchant data
  const { data, loading } = useQuery<GetMerchantQuery, GetMerchantQueryVariables>(GET_MERCHANT, {
    variables: { id: merchantId },
  });

  // Filter transactions for the specific merchant
  const { transactions, loading: transactionsLoading } = useFilteredTransactions({
    filters: [{ key: 'merchantId', value: merchantId || null }],
  });

  const merchant = data?.getMerchant;
  const recurringTransactions = merchant?.recurringTransactions ?? [];
  const hasRecurringTransactions = recurringTransactions.length > 0;

  const isIncomeMerchant = merchant?.isIncomeSource;

  const total = React.useMemo(() => {
    return calculateTotal(transactions, dateRange) * (isIncomeMerchant ? -1 : 1);
  }, [transactions, dateRange, isIncomeMerchant]);

  if (loading) {
    return <LoadingIndicator className="h-screen" size="lg" fullScreen />;
  }

  if (!merchant) {
    return <div>Merchant not found</div>;
  }

  return (
    <Entity.Pane>
      <MerchantPaneHeader merchant={merchant} dateRange={dateRange} onDateRangeChange={setDateRange} />
      <CalloutPaneBody
        carouselFilters={{ merchantId: merchant.id }}
        leftColumn={[
          <React.Fragment key="chart">
            <TransactionsChart
              transactions={transactions}
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
              value={total}
              isLoading={transactionsLoading}
              negateAmounts={isIncomeMerchant}
            >
              <TransactionChangeIndicator
                transactions={transactions}
                dateRange={dateRange}
                type={isIncomeMerchant ? TransactionType.Income : TransactionType.Spending}
              />
            </TransactionsChart>
            {hasRecurringTransactions && (
              <div className="flex flex-col gap-4">
                {recurringTransactions.map((stream) => (
                  <RecurringStreamTable
                    key={stream.id}
                    recurringStream={stream}
                    dateRange={dateRange}
                    onDateRangeChange={setDateRange}
                  />
                ))}
              </div>
            )}
          </React.Fragment>,
          <TransactionsTable
            key="table"
            merchantIds={[merchant.id]}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            primaryElement={TxnTableElementType.Account}
            excludeRecurring={hasRecurringTransactions}
            hideEmpty={hasRecurringTransactions}
          />,
        ]}
        rightColumn={
          <TransactionSummary
            transactions={transactions}
            dateRange={dateRange}
            type={isIncomeMerchant ? TransactionType.Income : TransactionType.Spending}
          />
        }
      />
    </Entity.Pane>
  );
};

export default MerchantPane;
